/* eslint-disable react-hooks/rules-of-hooks */
/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2025 - All Rights Reserved
###################################################################
*/

import React, { useEffect, useState } from "react";
import Button, { ButtonContainer } from 'components/Button';
import * as yup from 'yup';

import { GeneralSection } from '../../../../marketComposition/sections/index';
import { TaxRatesSection } from '../../taxRates/index';
import { InsurerTaxesSection } from '../../insurerTaxes/index';
import { FeePayableBySection } from '../../feePayableBy/index';
import { sortBy as _sortBy, cloneDeep } from "lodash";
import { useInsurerTaxes } from 'services/QuoteService';
import { groupMultipleFieldQuestions } from "../../../../marketComposition/handleQuestionChange";
import { onValidationError } from '../../../../paymentConfiguration/utils';

function RenderSection({
  disableAll,
  formikRef,
  values,
  setFieldValue,
  errors,
  setFieldError,
  idProduct,
  idOperation,
  idOperationParent,
  globalQuestionnaire,
  setGlobalQuestionnaire,
  questionnaireAnswersToDelete,
  setQuestionnaireAnswersToDelete,
  useGetQuestionnairesAnswersData,
  useGetQuestionnairesAnswersStatus,
  operation,
  loading,
  mc,
  recalculateButton = false,
  refreshTaxes = null,
  insurerGroup,
  feePayableByGroup,
  setInsurerGroup,
  setFeePayableByGroup,
  legacyFormSection,
  setTaxesLoading
}) {
  const {
    request: useInsurerTaxesRequest,
    loading: useInsurerTaxesLoading
  } = useInsurerTaxes(idOperation);
  const [insurersTaxes, setInsurersTaxes] = useState([]);
  const [canGroup, setCanGroup] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  const SECTIONS = {
    TAX_RATES: { name: "TAX_RATES", isMultiple: false, isRisk: false },
    INSURER_TAXES: { name: "INSURER_TAXES", isMultiple: true, isRisk: false },
    FEE_PAYABLE_BY: { name: "FEE_PAYABLE_BY", isMultiple: true, isRisk: false },
  };

  useEffect(() => {
    getInsurersTaxes();
  }, []);

  useEffect(() => {
    setTaxesLoading && setTaxesLoading(useInsurerTaxesLoading)
  }, [useInsurerTaxesLoading])

  useEffect(() => {
    if (globalQuestionnaire?.length && insurersTaxes.length >= 1 && idProduct) {
      handleQuestionnaire();
    }
  }, [insurersTaxes, operation]);

  useEffect(() => {
    buildValidation();
  }, [insurerGroup, feePayableByGroup])

  function getInsurersTaxes() {
    useInsurerTaxesRequest().then(({ data: res }) => {
      if (res.ret_code !== 200) {
        mc.error(res.ret_msg_details);
        return;
      }

      let d = res.ret_value.facilities.map((facility) => {
        const insurers = facility.insurers.sort((a, b) => String(a?.nmInsurer).localeCompare(String(b?.nmInsurer)));
        facility.insurers = insurers;
        return facility;
      });

      let canGroupTaxes = true;
      let taxesToGroup = {};

      ["clientTaxes", "insurerTaxes"].map((key) => {
        if (canGroupTaxes) {
          taxesToGroup[`${key}Qtd`] = [];
          taxesToGroup[`everyTaxesFrom${key}`] = [];
          d.map((fac) =>
            fac.insurers.map((insurer) => {
              if (taxesToGroup[`everyTaxesFrom${key}`].length == 0 && insurer[key]?.length) {
                taxesToGroup[`everyTaxesFrom${key}`].push(...insurer[key]);
              } else if (insurer[key]?.length) {
                insurer[key].map((taxClient) => {
                  let alreadyExistsTaxes = taxesToGroup[`everyTaxesFrom${key}`].find(
                    (tax) => tax.feeType == taxClient.feeType && tax.feeValue == taxClient.feeValue
                  );
                  if (!alreadyExistsTaxes) {
                    canGroupTaxes = false;
                  }
                });
              }
              taxesToGroup[`${key}Qtd`].push(insurer[key]?.length ? insurer[key].length : 0);
            })
          );

          canGroupTaxes = canGroupTaxes ? [...new Set(taxesToGroup[`${key}Qtd`])].length == 1 : false;
        }
      });

      setTimeout(() => {
        setCanGroup(canGroupTaxes);
        setInsurersTaxes(d);
      }, 300);
    });
  }

  function handleQuestionnaire() {
    let questionnaire = globalQuestionnaire;

    if (questionnaire && questionnaire.length > 0) {
      const initialCacheTaxes = questionnaire.filter((q) => q.idQuestion === 1378 && q.idQuestionnaireAnswers);

      // FEE_PAYABLE_BY - Initial adjustments
      const feePayableByGroupQuestions = GeneralSection.events.groupMultipleFieldQuestions({
        mainIdQuestion: 1464,
        questionnaire,
        handleQuestionsInitialAdjustments: FeePayableBySection.events.handleInitialAdjustments,
      })

      let forAllUnderwritersAndFacilities = questionnaire.find((q) => q.idQuestion === 1377);
      let existsInsurersAnswers = questionnaire.find((q) => q.idQuestion === 1378 && q?.answer)?.answer;
      let insurers = groupMultipleFieldQuestions(1378, questionnaire, operation, false, insurersTaxes.length === 0);
      let isGrouped = forAllUnderwritersAndFacilities?.idAnswerOption === 6809;
      setIsGrouped(isGrouped);

      if (!existsInsurersAnswers && insurersTaxes.length) {
        InsurerTaxesSection.events.handleTaxes({
          answerTax: forAllUnderwritersAndFacilities,
          insurersAll: insurers,
          questionnaireAnswers: questionnaire,
          insurerGroup: insurers,
          setInsurerGroup,
          feePayableByGroup: feePayableByGroupQuestions,
          setFeePayableByGroup,
          insurersTaxes,
          canGroup,
          setCanGroup,
          setIsGrouped,
          globalQuestionnaire: questionnaire,
          setGlobalQuestionnaire: setGlobalQuestionnaire,
          questionnaireAnswersToDelete,
          setQuestionnaireAnswersToDelete,
          operation,
          loading,
          mc,
        });

        if (initialCacheTaxes?.length) {
          setQuestionnaireAnswersToDelete([...questionnaireAnswersToDelete, ...initialCacheTaxes.map((q) => q.idQuestionnaireAnswers)]);
        }
      } else {
        const insurerAnswers = insurers.flatMap(arr => arr).filter(question => question.idQuestion === 1378);
        const insurerId = insurerAnswers.map(item => item?.answer ? JSON.parse(item.answer)?.id : null);
        const hasAllInsurers = allInsurersPresent(insurerAnswers, insurersTaxes);

        if (hasAllInsurers || (canGroup && insurerId && insurerId[0] === -1)) {
          insurers.forEach((taxRow) => InsurerTaxesSection.events.handleInitialAdjustments({
            questionnaire: taxRow,
            operation
          }));

          insurers = _sortBy(insurers, function (item) {
            return [item[0].answer, item[1].answer, item[2].answer];
          });

          setCanGroup(isGrouped || canGroup);
          setInsurerGroup(JSON.parse(JSON.stringify(insurers || [])));

          FeePayableBySection.events.handleGroup({
            insurerGroup: insurers,
            feePayableByGroup: feePayableByGroupQuestions,
            setFeePayableByGroup,
            questionnaireAnswersToDelete,
            setQuestionnaireAnswersToDelete,
          })
        } else {
          const questionsToDelete = [...questionnaireAnswersToDelete, ...initialCacheTaxes.map((q) => q.idQuestionnaireAnswers)]

          InsurerTaxesSection.events.handleTaxes({
            answerTax: forAllUnderwritersAndFacilities,
            insurersAll: insurers,
            questionnaireAnswers: questionnaire,
            insurerGroup: insurers,
            setInsurerGroup,
            feePayableByGroup: feePayableByGroupQuestions,
            setFeePayableByGroup,
            insurersTaxes,
            canGroup,
            setCanGroup,
            setIsGrouped,
            globalQuestionnaire: questionnaire,
            setGlobalQuestionnaire: setGlobalQuestionnaire,
            questionnaireAnswersToDelete: questionsToDelete,
            setQuestionnaireAnswersToDelete,
            operation,
            loading,
            mc,
          });
        }
      }
      setGlobalQuestionnaire(JSON.parse(JSON.stringify(globalQuestionnaire)));
    }
  }

  function allInsurersPresent(insurers, insurerTaxes) {
    const answerValues = insurers.map(item => item?.answer ? JSON.parse(item.answer)?.id : null).filter(i => i !== -1);
    const insurerIds = insurerTaxes.flatMap(insurer => insurer.insurers.map(insurerDetails => insurerDetails.idPersonInsurer));

    return answerValues.every(answerValue => insurerIds.includes(answerValue)) && insurerIds.every(answerValue => answerValues.includes(answerValue));
  }

  const buildValidation = () => {
    const validationSchema = {};

    const questionnaire = handleSaveQuestionnaireAnswer();

    for (const question of questionnaire) {
      if (!question.key) continue;
      if (!question?.behaviorObject?.sectionName || !SECTIONS[question?.behaviorObject?.sectionName]) continue;
      validationSchema[question.key] = question.visibleQuestion && !question.answer && question.behaviorObject.isRequired
        ? yup.string().required()
        : yup.string().notRequired().nullable();
    }
    return yup.object().shape(validationSchema);
  };

  const handleSaveQuestionnaireAnswer = (onlyQuestions = true) => {
    const body = { questionnaireAnswers: cloneDeep(globalQuestionnaire) }

    for (const [key, section] of Object.entries(SECTIONS)) {
      if (section.isMultiple) {
        let multipleFields = []
        switch (section.name) {
          case 'FEE_PAYABLE_BY':
            multipleFields = cloneDeep(feePayableByGroup);
            break;
          case 'INSURER_TAXES':
            multipleFields = cloneDeep(insurerGroup);
            break;
          default:
            multipleFields = [];
            break;
        }

        multipleFields.map(group => {
          if (group?.length) {
            const rootQuestion = group.find(question => !question.idQuestionRoot);
            const childrenQuestions = group.filter(question => question.idQuestionRoot);
            rootQuestion.childrenQuestions = childrenQuestions;
            if (!onlyQuestions) {
              body.questionnaireAnswers = [...body.questionnaireAnswers, rootQuestion];
            } else {
              body.questionnaireAnswers = [...body.questionnaireAnswers, ...group];
            }
          }
        })
      }
    }

    body.questionnaireAnswers = body.questionnaireAnswers.filter(question => {
      return (question?.behaviorObject?.sectionName && SECTIONS[question?.behaviorObject?.sectionName]);
    });

    let childrenQuestionIds = [];
    for (const question of body.questionnaireAnswers) {
      if ((question.idDomainQuestion === 270 ||
        question.idDomainQuestion === 271 ||
        (question.idDomainQuestion === 44 && typeof question.answer === 'object')
      ) && question.answer) {
        try {
          question.answer = JSON.stringify(question.answer);
        } catch (e) {
        }
      }

      if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
        if (question?.childrenQuestions?.length) {
          const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
          childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
        }
      } else {
        question.childrenQuestions = [];
      }
    }

    if (onlyQuestions) return body.questionnaireAnswers;
  };

  return (
    <>
      <TaxRatesSection.components.RenderSection
        disableAll={disableAll || !canGroup}
        formikRef={formikRef}
        values={values}
        setFieldValue={setFieldValue}
        errors={errors}
        setFieldError={setFieldError}
        idProduct={idProduct}
        idOperation={idOperation}
        idOperationParent={idOperationParent}
        globalQuestionnaire={globalQuestionnaire}
        setGlobalQuestionnaire={setGlobalQuestionnaire}
        questionnaireAnswersToDelete={questionnaireAnswersToDelete}
        setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
        insurerGroup={insurerGroup}
        setInsurerGroup={setInsurerGroup}
        feePayableByGroup={feePayableByGroup}
        setFeePayableByGroup={setFeePayableByGroup}
        insurersTaxes={insurersTaxes}
        canGroup={canGroup}
        setCanGroup={setCanGroup}
        setIsGrouped={setIsGrouped}
        operation={operation}
        loading={loading}
        mc={mc}
        legacyFormSection={legacyFormSection}
      />

      <InsurerTaxesSection.components.RenderSection
        disableAll={disableAll}
        values={values}
        setFieldValue={setFieldValue}
        errors={errors}
        setFieldError={setFieldError}
        formikRef={formikRef}
        idProduct={idProduct}
        idOperation={idOperation}
        idOperationParent={idOperationParent}
        globalQuestionnaire={globalQuestionnaire}
        setGlobalQuestionnaire={setGlobalQuestionnaire}
        questionnaireAnswersToDelete={questionnaireAnswersToDelete}
        setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
        insurerGroup={insurerGroup}
        setInsurerGroup={setInsurerGroup}
        feePayableByGroup={feePayableByGroup}
        setFeePayableByGroup={setFeePayableByGroup}
        insurersTaxes={insurersTaxes}
        isGrouped={isGrouped}
      />

      <FeePayableBySection.components.RenderSection
        disableAll={disableAll}
        values={values}
        setFieldValue={setFieldValue}
        errors={errors}
        setFieldError={setFieldError}
        formikRef={formikRef}
        idProduct={idProduct}
        idOperation={idOperation}
        idOperationParent={idOperationParent}
        feePayableByGroup={feePayableByGroup}
        setFeePayableByGroup={setFeePayableByGroup}
        setGlobalQuestionnaire={setGlobalQuestionnaire}
      />

      {recalculateButton &&
        <ButtonContainer style={{ flexDirection: "row-reverse" }}>
          <Button
            third
            style={{ marginLeft: "10px", textAlign: "center", marginTop: "5px", background: "#3A5DAE" }}
            onClick={() => {
              buildValidation().validate(values, { abortEarly: false })
                .then(() => {
                  refreshTaxes();
                })
                .catch((e) => {
                  onValidationError(e, mc, formikRef, globalQuestionnaire, [...insurerGroup, ...feePayableByGroup]);
                  loading.stop();
                });
            }}
          >
            RECALCULATE
          </Button>
        </ButtonContainer>
      }
    </>
  );
};

const components = {
  RenderSection
}

export { components }